import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { Check,  X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '@/components/common/text-button';
import { SearchBtnIcon } from '@/components/agent-dashboard/assets';
import { TextInput } from '@/components/common/text-input';
import { TruncatedText } from '@/components/common/truncated-text';
import { Image } from '@/components/common/image';
import { CreateActorButton } from '../custom-digital-human';
import { ActorCard } from './actor-card';
import { EditVideoPlayModal } from '../modal-edit-video-play';
import { AllActorModal } from './all-actor-modal';
import { useToast } from '@/hooks/use-toast';
import { agentTemplateHooks, useUserManagementHooks } from '@/manager';
import { LoadingOverlay } from '../common/loading-overlay';
import { AgentUserActorData } from '@/manager/manager/agent-dashboard-user-management-manager';
import { AddTemplateActiveIcon } from './assets';
import { RadioButton } from '../common/radio-button';
import { LarkImageTableItemsData } from '@/services/agent-v2-feishu-tuwen-table-data';

interface TemplateImageAllModalProps {
  trigger?: React.ReactElement;
  title?: string;
  open?: boolean;
  loading?: boolean;
  onSave?: (data: LarkImageTableItemsData) => void;
  // onUpdate?: (data: LarkImageTableItemsData) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

/** 图文-全部模板弹窗 */
export const TemplateImageAllModal: React.FC<TemplateImageAllModalProps> = ({
  trigger,
  title,
  open: externalOpen,
  onOpenChange,
  loading: externalLoading,
  onSave,
  // onUpdate,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [internalOpen, setInternalOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null); // 用于聚焦标题的ref

  const {
    loading,
    larkImageTemplates,
    larkImageTemplateSearchFormData,
    changeSearchForm,
    resetAndSearch,
    search,
  } = agentTemplateHooks.useLarkImageTemplatesData();

  const [initialSearch, setInitialSearch] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = async (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
    setSelectedId(null);
  };

  const handleInitSearch = async () => {
    try {
      setInitialSearch(true)
      await search(1);
    } catch (error) {
      console.log('handleInitSearch error', error);
    } finally {
      setInitialSearch(false);
    }
  }

  useEffect(() => {
    if (currentOpen) {
      handleInitSearch()
    } else {
      resetAndSearch();
    }
  }, [currentOpen]);


  // 处理风格选择
  const handleSelect = (id: string) => {
    setSelectedId(id);
  };

  const handleSearch = async () => {
    try {
      await search(1);
    } catch (error) {
      console.warn(error);
    } finally {
      setSelectedId(null);
    }
  }

  const handleSave = async () => {
    if (!selectedId) {
      toast({
        description: t('agent_dashboard.user_detail.larkImageTemplate.add_modal.select_tips'),
        variant: 'destructive',
      });
      return;
    }
    onSave?.(larkImageTemplates.find(item => item.fields.templateId === selectedId));
    handleClose();
  }

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [currentOpen]);

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[750px] h-[755px]  flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-6  border-b border-[rgba(233,234,235,1)] pb-5">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-semibold flex items-center'
          >
            <div className='size-12 flex justify-center items-center rounded-lg mr-4 border border-[rgba(213,215,218,1)]'>
              <AddTemplateActiveIcon className='size-6' />
            </div>
            <span>{title || t('agent_dashboard.user_detail.template.add_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
            </button>
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='relative flex-1 flex flex-col px-6 pt-5 select-none mb-5 overflow-hidden'>
          {/* <div className='flex items-center justify-between mb-4'>
            <span className='text-[rgba(24,29,39,1)] text-base font-medium'>
              {t('agent_dashboard.user_detail.template.add_modal.choose_style')}
            </span>
          </div> */}
          <div className='flex items-center gap-4 mb-5'>
            <TextInput
              type="text"
              placeholder={t('agent_dashboard.user_detail.larkImageTemplate.add_modal.search_placeholder')}
              value={larkImageTemplateSearchFormData.templateId}
              className='h-10'
              onChange={(e) => changeSearchForm('templateId', e.target.value)}
            />
            <TextButton
              label={t('agent_dashboard.user_detail.template.add_modal.search')}
              icon={<SearchBtnIcon className='w-5 h-5' />}
              className={cn(
                '!w-[106px] h-9 ',
                'text-white font-semibold text-sm',
              )}
              onClick={handleSearch}
            />
          </div>
          {initialSearch && <Skeleton />}
          {!initialSearch && (
            <div className='relative pt-0.5 pl-0.5 flex-1 flex flex-col h-full overflow-y-auto'>
              <div className='grid grid-cols-6 gap-3 relative'>
                {larkImageTemplates?.map((item, index) => (
                  <div key={item.fields.templateId} className="w-full h-full relative">
                    <div
                      className={cn(
                        "w-[102px] h-[136px] group relative rounded-[12px] transition-all cursor-pointer p-0.5 overflow-hidden select-none",
                        selectedId === item.fields.templateId
                          ? "outline outline-2 outline-i-normal"
                          : "outline outline-transparent"
                      )}
                      onClick={() => handleSelect(item.fields.templateId)}
                    >
                      <div className='size-full overflow-hidden flex-shrink-0'>
                        <Image src={item.fields.fileurl} className="size-full rounded-[12px] object-cover" />
                      </div>
                      {/* 选中样式 */}
                      <div
                        className={cn(
                          "size-11 bg-black  absolute -bottom-5 -right-5 opacity-0 transition-all duration-150 rounded-tl-[18.4px] rounded-br-[10px] rounded-tr-none rounded-bl-none",
                          selectedId === item.fields.templateId && "opacity-100"
                        )}
                      >
                      </div>
                      <Check className={cn(
                        "size-4 text-white absolute bottom-0.5 right-1 font-bold opacity-0",
                        selectedId === item.fields.templateId && "opacity-100"
                      )} />
                    </div>
                    <div className="w-full text-center mt-3">
                      <div className="w-full">
                        <TruncatedText text={item.fields.templateName} className='max-w-[90%] text-base font-medium text-[rgba(16,24,40,1)]' />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <LoadingOverlay loading={!initialSearch && loading} />
        </div>
        {/* 操作按钮区域 */}
        <div className='w-full h-[93px] flex justify-end p-6 gap-2 border-t border-[rgba(233,234,235,1)]'>
          <div className='flex items-center gap-2'>
            <TextButton
              label={t('agent_dashboard.user_detail.template.add_modal.cancel')}
              className='w-[135px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
              onClick={handleClose}
            />
            <TextButton
              label={t('agent_dashboard.user_detail.template.add_modal.save')}
              className='w-[135px] h-11'
              onClick={handleSave}
              loading={externalLoading}
              disabled={externalLoading}
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}
const Skeleton: React.FC = () => {
  return (
    <div className="grid grid-cols-6 gap-3 relative">
      {Array.from({ length: 12 }).map((_, index) => (
        <div key={index} className="w-full h-full relative">
          <div
            className="w-[102px] h-[136px] p-0.5 relative rounded-[12px] overflow-hidden select-none animate-pulse"
          >
            {/* 卡片骨架 */}
            <div className="size-full bg-gray-200 rounded-[12px]"></div>
          </div>
          {/* 标题骨架 */}
          <div className="w-full text-center mt-3">
            <div className="w-[80%] h-6 bg-gray-200 rounded mx-auto animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );
};